import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTable } from 'react-table';

import { CopyToClipboard } from '@utilities';
import { getCopyTableValue } from '@utilities/helpers';

import { DISPLAY_AS } from '../../utilities/helpers';
import CollapsibleTable from '../CollapsibleTable';

import styles from './_index.module.scss';

const ContSum = ({ compareByGroups, data, displayAs = DISPLAY_AS.PERCENT.value }) => {
  const isDisplayAsPercent = displayAs === DISPLAY_AS.PERCENT.value;
  const tableToDisplay = isDisplayAsPercent ? data?.tablePct : data?.tableCount;

  const { footerGroups, getTableProps, headerGroups, prepareRow, rows } = useTable({
    columns: tableToDisplay?.columns,
    data: tableToDisplay?.data,
  });

  const alignLeftColumns = ['option_value', 'compare_by_breakout'];

  return (
    <div className={styles['cont-sum']}>
      <div className={styles['cont-sum-table-actions']}>
        <CopyToClipboard
          text="Copy Table"
          value={getCopyTableValue({ columns: tableToDisplay?.columns, data: rows })}
        />
      </div>
      <CollapsibleTable rowCount={rows.length}>
        <table className={styles['cont-sum-table']} {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className={classNames({
                      [styles['cont-sum-align-right']]: !alignLeftColumns.includes(column.id),
                      [styles['cont-sum-compare-by-header']]: column.id === 'compare_by_breakout',
                    })}
                    key={column.id}
                    title={column.render('Header')}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {rows?.map((row, rowIndex) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={rowIndex}>
                  {row?.cells?.map((cell) => {
                    const cellProps = cell.getCellProps({});
                    if (cell.column?.rowSpanBy) {
                      const rowSpanByValue = row.original[cell.column.rowSpanBy];
                      const rowCount = rows.filter(
                        ({ original }) => original[cell.column.rowSpanBy] === rowSpanByValue
                      ).length;

                      const firstRowIndex = rows.findIndex(
                        ({ original }) => original[cell.column.rowSpanBy] === rowSpanByValue
                      );
                      if (rowCount > 1 && rowIndex === firstRowIndex) {
                        cellProps.rowSpan = rowCount;
                      } else if (
                        rowCount > 1 &&
                        firstRowIndex < rowIndex &&
                        rowIndex < firstRowIndex + rowCount
                      ) {
                        // display none keeps 'nth-child' styles working
                        cellProps.style = { display: 'none' };
                      }
                    }

                    const isCompareByBreakout = cell.column.id === 'compare_by_breakout';
                    return (
                      <td
                        {...cellProps}
                        className={classNames({
                          [styles['cont-sum-align-right']]: !alignLeftColumns.includes(
                            cell.column.id
                          ),
                          [styles['cont-sum-compare-by-breakout']]: isCompareByBreakout,
                        })}
                        key={cell.id}
                      >
                        {isCompareByBreakout && (
                          <span
                            className={styles['cont-sum-table-dot']}
                            style={{
                              backgroundColor: compareByGroups?.find(({ id, name }) =>
                                [id, name].includes(cell.value)
                              )?.color,
                            }}
                          />
                        )}
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            {footerGroups.map((group, index) => (
              <tr {...group.getFooterGroupProps()} key={index}>
                {group.headers.map((column) => (
                  <td
                    {...column.getFooterProps()}
                    className={classNames({
                      [styles['cont-sum-align-right']]: !alignLeftColumns.includes(column.id),
                    })}
                    key={column.id}
                  >
                    {column.render('Footer')}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
      </CollapsibleTable>
    </div>
  );
};
ContSum.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  displayAs: PropTypes.string,
};

export default ContSum;
